import { useEffect, useLayoutEffect, useRef } from "react";
import { enterSends as resolveEnterSends } from "../lib/preferences";
import type { OutgoingAttachment } from "../openclaw/attachments";
import { useT } from "../lib/useT";
import type { MessageKey } from "../lib/i18n";
import { useStore, type QueueMode } from "../state/store";
import { DraftAttachments } from "./Attachments";
import { IconPaperclip, IconSend, IconStop } from "./icons";

const QUEUE_MODES: Array<[QueueMode, MessageKey, MessageKey]> = [
  ["steer", "composer.steer", "composer.steerHint"],
  ["followup", "composer.followup", "composer.followupHint"],
  ["interrupt", "composer.interrupt", "composer.interruptHint"],
];

/**
 * Message composer.
 *
 * Enter sends on a keyboard device and inserts a newline on a touch device,
 * which is the convention every mobile messenger follows — a phone keyboard has
 * no comfortable Shift+Enter.
 *
 * Pictures reach it three ways, because on three devices people reach for three
 * different ones: the clip opens the picker on a phone, Ctrl+V pastes a
 * screenshot on a desktop, and a drop lands a file from a folder.
 */
export function Composer({
  value,
  onChange,
  onSend,
  onStop,
  onAttach,
  onRemoveAttachment,
  attachments,
  queueMode,
  onQueueModeChange,
  busy,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  onAttach: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;
  attachments: OutgoingAttachment[];
  queueMode: QueueMode;
  onQueueModeChange: (mode: QueueMode) => void;
  busy: boolean;
  disabled: boolean;
  placeholder: string;
}) {
  const t = useT();
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const enterPreference = useStore((state) => state.preferences.enter);
  const enterSends = useRef(true);

  useEffect(() => {
    enterSends.current = resolveEnterSends(enterPreference);
  }, [enterPreference]);

  // Grow with the text up to the CSS max-height, then scroll inside.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  }, [value]);

  const canSend = (value.trim().length > 0 || attachments.length > 0) && !disabled;

  return (
    <div className="composer">
      {/* While a run is going, a send has to say what it means by it: fold into
          the running turn, queue behind it, or cut it off. */}
      {busy ? (
        <div className="queue-modes" role="radiogroup" aria-label={t("composer.queueLabel")}>
          {QUEUE_MODES.map(([mode, label, hint]) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={queueMode === mode}
              className={`queue-mode${queueMode === mode ? " is-active" : ""}`}
              onClick={() => onQueueModeChange(mode)}
              title={t(hint)}
            >
              {t(label)}
            </button>
          ))}
        </div>
      ) : null}
      <DraftAttachments attachments={attachments} onRemove={onRemoveAttachment} />
      <div className="composer-inner">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="visually-hidden"
          tabIndex={-1}
          onChange={(event) => {
            const files = [...(event.target.files ?? [])];
            if (files.length) {
              onAttach(files);
            }
            // Reset, so picking the same file twice in a row still fires.
            event.target.value = "";
          }}
        />
        <button
          type="button"
          className="attach-button"
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
          aria-label={t("composer.attach")}
          title={t("composer.attach")}
        >
          <IconPaperclip />
        </button>
        <textarea
          ref={ref}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onPaste={(event) => {
            const files = [...event.clipboardData.files].filter((file) =>
              file.type.startsWith("image/"),
            );
            if (files.length) {
              // A pasted screenshot is an attachment, not the text of its path.
              event.preventDefault();
              onAttach(files);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && enterSends.current) {
              event.preventDefault();
              if (canSend) {
                onSend();
              }
            }
          }}
          placeholder={placeholder}
          rows={1}
          disabled={disabled}
          aria-label={t("composer.message")}
        />
        {busy && !canSend ? (
          <button
            type="button"
            className="send-button is-stop"
            onClick={onStop}
            aria-label={t("composer.stop")}
            title={t("composer.stop")}
          >
            <IconStop />
          </button>
        ) : (
          <button
            type="button"
            className="send-button"
            onClick={onSend}
            disabled={!canSend}
            aria-label={t("composer.send")}
            title={t("composer.send")}
          >
            <IconSend />
          </button>
        )}
      </div>
    </div>
  );
}
